/* eslint-disable no-console */
import React, { useState } from 'react';
import Icon from '@mdi/react';
import { Link, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Alert, Chip } from 'daaas-ui';
import LockIcon from '@mui/icons-material/Lock';
import {
  mdiCubeSend,
  mdiFileTableBoxMultipleOutline,
  mdiMapMarkerPath,
  mdiServerNetwork,
  mdiTools,
} from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { useIsAuthenticated } from '@azure/msal-react';

// Components
import ToolCard from '../components/UI/ToolCard';
import ContentContainer from '../components/Layout/ContentContainer';
import ContentSection from '../components/Layout/ContentSection';
import Banner from '../components/UI/Banner';
import RequestKubeflowNamespace from '../components/UI/Dialogs/RequestKubeflowNamespace';
import BrandingDAS from '../components/Layout/BrandingDAS';

// Logos
import chartTimelineVariantShimmer from '../assets/images/chartTimelineVariantShimmer.png';
import tableKey from '../assets/images/tableKey.png';

const Root = styled('div')(({ theme }) => ({
  '.MuiChip-outlinedPrimary': {
    border: '3px solid',
    borderColor: `${theme.palette.primary.main} !important`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
    borderTopLeftRadius: theme.spacing(0.625),
    borderTopRightRadius: theme.spacing(0.625),
    borderBottomLeftRadius: theme.spacing(0.625),
    borderBottomRightRadius: theme.spacing(0.625),
    '& .MuiChip-label': {
      fontWeight: 'bolder',
      fontSize: '1rem',
    },
  },
  '& .banner__header': {
    fontSize: `3.5rem !important`,
    lineHeight: '1.275',
    letterSpacing: '0.47',
    [theme.breakpoints.up('sm')]: {
      fontSize: `5rem !important`,
    },
  },
}));

export default function AAW() {
  const { t, i18n } = useTranslation(['aaw', 'common']);
  const isAuthenticated = useIsAuthenticated();

  // Translations
  React.useEffect(() => {
    setTimeout(() => {
      document.title = `${t('common:daaas')} - ${t('common:aaw')}`;
      document
        .querySelector("meta[name='description']")
        .setAttribute('content', t('aaw:description'));
      document
        .querySelector("meta[name='keywords']")
        .setAttribute('content', t('aaw:keywords'));
      document
        .querySelector("link[rel='canonical']")
        .setAttribute('href', t('aaw:canonical'));
      document
        .querySelector("meta[name='dcterms.title']")
        .setAttribute('content', t('aaw:dctermsTitle'));
      document
        .querySelector("meta[name='dcterms.description']")
        .setAttribute('content', t('aaw:dctermsDescription'));
      document
        .querySelector("meta[name='dcterms.creator']")
        .setAttribute('content', t('common:dctermsCreator'));
      document
        .querySelector("meta[name='dcterms.language']")
        .setAttribute('content', t('common:dctermsLanguage'));
      document
        .querySelector("meta[name='dcterms.subject']")
        .setAttribute('content', t('common:dctermsSubject'));
    }, '100');
  }, [i18n.language]);

  // Kubeflow request namespace trigger functionality
  const [kubeflowDialogStatus, setkubeflowDialogStatus] = useState(false);

  const toggleKubeflowDialog = () => {
    setkubeflowDialogStatus(
      (prevKubeflowDialogStatus) => !prevKubeflowDialogStatus
    );
  };

  const handleKubeflowRequestNamespaceClick = () => {
    toggleKubeflowDialog();
  };

  return (
    <Root>
      <Banner>
        <Typography
          className="banner__header"
          variant="h1"
          color="textPrimary"
          sx={{ py: 2 }}
        >
          {t('common:aaw')}
        </Typography>
      </Banner>
      <ContentContainer paddingBottom={false}>
        <ContentSection>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t('bannerHeading')}
          </Typography>
          <Typography sx={{ mb: 2 }} color="textPrimary">
            {t('bannerPara1')}
          </Typography>
          <ul>
            <li>
              <Typography>{t('bannerList1_1')}</Typography>
            </li>
            <li>
              <Typography>{t('bannerList1_2')}</Typography>
            </li>
            <li>
              <Typography>{t('bannerList1_3')}</Typography>
            </li>
            <li>
              <Typography>{t('bannerList1_4')}</Typography>
            </li>
            <li>
              <Typography>{t('bannerList1_5')}</Typography>
            </li>
          </ul>

          <Alert open severity="info" closeable={false} sx={{ mt: 2, mb: 2 }}>
            {t('alertPara1_1')}
            <Link
              href={t('alertLink1_HREF')}
              rel="noopener noreferrer"
              color="inherit"
            >
              {t('alertLink1_Text')}
            </Link>
            {t('alertPara1_2')}
          </Alert>
          <Grid container wrap="nowrap" alignItems="center">
            <Grid item>
              <Chip
                color="primary"
                label={t('common:protectedB')}
                icon={<LockIcon />}
                variant="outlined"
              />
            </Grid>
            <Grid item>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {t('aawToolTip')}
              </Typography>
            </Grid>
          </Grid>
        </ContentSection>
        <ContentSection
          heading={t('section1Heading')}
          subheading={t('section1Subheading')}
        >
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4}>
              {isAuthenticated ? (
                <ToolCard
                  bordered
                  icon={<Icon path={mdiCubeSend} />}
                  heading={{
                    text: t('toolKubeflowTitle'),
                    linkTo: t('toolKubeflowLink'),
                  }}
                  links={[
                    {
                      text: t('toolKubeflowMenu1Title'),
                      linkFunction: handleKubeflowRequestNamespaceClick,
                    },
                  ]}
                >
                  <Typography variant="body1">
                    {t('toolKubeflowDescription')}
                  </Typography>
                </ToolCard>
              ) : (
                <ToolCard
                  bordered
                  icon={<Icon path={mdiCubeSend} />}
                  heading={{
                    text: t('toolKubeflowTitle'),
                    linkTo: t('toolKubeflowLink'),
                  }}
                >
                  <Typography variant="body1">
                    {t('toolKubeflowDescription')}
                  </Typography>
                </ToolCard>
              )}
              <RequestKubeflowNamespace
                open={kubeflowDialogStatus}
                toggleDialog={toggleKubeflowDialog}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                bordered
                icon={<Icon path={mdiFileTableBoxMultipleOutline} />}
                heading={{
                  text: t('toolBlobCsiTitle'),
                }}
                // Commented out for now cause link broken
                links={[
                  {
                    linkTo: t('toolBlobCsiInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('toolBlobCsiTitle')}
                        </span>
                      </>
                    ),
                  },
                ]}
              >
                <Typography variant="body1">
                  {t('toolBlobCsiDescription')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                icon={<Icon path={mdiTools} />}
                heading={{
                  text: t('toolPAASTitle'),
                }}
                links={[
                  {
                    linkTo: t('toolPAASInfoLink'),
                    text: (
                      <>
                        {t('common:documentation')}
                        <span className="screen-reader-text">
                          - {t('toolPAASTitle')}
                        </span>
                      </>
                    ),
                  },
                ]}
              >
                <Typography variant="body1">
                  {t('toolPAASDescription')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                icon={<Icon path={mdiServerNetwork} />}
                heading={{
                  text: t('toolGrafanaTitle'),
                  linkTo: t('toolGrafanaLink'),
                }}
              >
                <Typography variant="body1">
                  {t('toolGrafanaDescription')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                icon={
                  <img
                    src={chartTimelineVariantShimmer}
                    alt=""
                    loading="lazy"
                  />
                }
                heading={{
                  text: t('toolKubecostTitle'),
                }}
              >
                <Typography variant="body1">
                  {t('toolKubecostDescription')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                icon={<Icon path={mdiMapMarkerPath} />}
                heading={{
                  text: t('toolKibanaTitle'),
                  linkTo: t('toolKibanaLink'),
                }}
              >
                <Typography variant="body1">
                  {t('toolKibanaDescription')}
                </Typography>
              </ToolCard>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ToolCard
                icon={<img src={tableKey} alt="" loading="lazy" />}
                heading={{
                  text: t('toolVaultTitle'),
                  linkTo: t('toolVaultLink'),
                }}
              >
                <Typography variant="body1">
                  {t('toolVaultDescription')}
                </Typography>
              </ToolCard>
            </Grid>
          </Grid>
        </ContentSection>
      </ContentContainer>
      <BrandingDAS />
    </Root>
  );
}
